// Front-line ribbons: the sourced polylines of snapshot.fronts, draped on the terrain as a glowing
// band in the controller's colour with a hot core, scorched ground and a few embers close to it.
// A line older than STALE_DAYS is drawn as a plain grey band with no glow: it says where the front
// was, not where it is.
//
//   snapshot.fronts ─▶ world polylines ─▶ CPU distance field (R8 texture, one per front)
//   terrain fragment ─▶ sample field ─▶ d ─┬─ scorch   ground darkens within SCORCH_REACH
//                                          ├─ body     controller colour, width BODY_W
//                                          ├─ core     hot yellow-white, width CORE_W
//                                          └─ halo     emissive falloff (bloom)
//
// The field makes the ribbon follow any terrain height and LOD with no mesh to rebuild. Nothing is
// drawn beyond HALO_REACH of a sourced line; embers rise only from the line itself.
// Truth: every front carries its sources; a front without any is not drawn (and would count as an
// unsourced marker if it were).

import * as THREE from 'three/webgpu';
import { float, floor, fract, length, mix, mod, select, sin, smoothstep, texture, uniform, vec2, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import type UniformNode from 'three/src/nodes/core/UniformNode.js';
import { WORLD_W, hexAtWorld } from '../../core/grid.ts';
import type { SourceId } from '../../core/schema.ts';
import type { WorldSnapshot } from '../../world/api.ts';
import type { SurfaceCtx, SurfaceLayer } from '../surface.ts';
import { rgb } from '../tsl/color.ts';
import { hash12, worldNoise } from '../tsl/noise.ts';
import { ATLAS_H, CHANNELS, FIELD_SIZE, type Atlas } from './atlas.ts';
import { contactLines, fieldBox, lineLength, rasterField, toWorld, type FieldBox, type WorldLine } from './frontGeo.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

// One field holds every ribbon (in the political atlas, atlas.ts): the terrain shader's
// sampled-texture budget is small. Ribbons share it when they fit the box and the actor.
const MAX_SPAN = 60; // widest box (world units) the ribbons may share: texel = span / FIELD_SIZE
const FIELD_REACH = 2; // world units the field stores; 255 = this far or more (COVER_FAR must fit)
const TAPER_CHANNEL = 1; // atlas channel of a line's end taper: 255 full width, 0 gone
// While the date is dragged, the field is a sketch in the corner of its block, this many texels a
// side. Coarser and the ribbon breaks into dashes: the line can lie half a texel from every centre,
// and that distance must stay under BODY_W.
export const SKETCH_SIZE = 512;
export const SCRUB_MS = 300; // snapshots this close together are a drag (the time slider sends one per 120 ms)
const INACTIVE_Z = -1e5; // box centre of an empty slot: every fragment falls outside it
const FAR = 9;

// Ribbon look (world units, with pixel floors so it stays a line when zoomed out).
const BODY_W = 0.075;
const BODY_MIN_PX = 2.5;
const CORE_W = 0.022;
const CORE_MIN_PX = 0.9;
const CASING_SCALE = 2.2; // dark band round the body, so it reads against a similar fill
const CASING_STRENGTH = 0.8;
const HALO_REACH = 0.7;
const SCORCH_REACH = 0.55;
const SCORCH_STRENGTH = 0.35;
const SCORCH_TINT = [0.3, 0.24, 0.22] as const;
const BODY_LIGHTEN = 0.05;
// Glow feeds the bloom: kept low enough that the core stays a warm gold, not a white blot.
const BODY_GLOW = 0.3;
const CORE_GLOW = 0.95;
const HALO_GLOW = 0.21;
const DEFAULT_COLOR = '#ff6a2b';
const HOT_COLOR = '#fff1c4';
// An old line: grey, cold, and a fraction of the glow.
const STALE_COLOR = '#8b9098';
const STALE_GLOW = 0.2;

// Heat flicker: noise sampled along a drift, so brightness ripples down the line.
const HEAT_FREQ = 2.4;
const HEAT_MIN = 0.65;
const HEAT_DRIFT = [0.45, 0.3] as const;
const BREATH_HZ = 0.35;
const BREATH = 0.12;

// Embers: one dot per lattice cell, on a fraction of the cells, drifting north.
const EMBER_FREQ = 6;
const EMBER_RISE = 0.22;
const EMBER_DENSITY = 0.86; // cells with a hash above this are alive
const EMBER_SIZE = 0.2; // in cells
const EMBER_REACH = 0.5;
const EMBER_GLOW = 1.2;
const EMBER_FADE_PX = [0.05, 0.02] as const; // world units per pixel: embers vanish past the first

// Cover of the hex-edge front that a ribbon replaces (borders.ts). Wide enough to take the short
// stubs the ribbon's cleaning leaves a hex off the line, which read as glitches hanging off it: a
// stub whose middle is 1.1 from the line has its far end 1.4 to 1.8 from it.
const COVER_NEAR = 1.5;
const COVER_FAR = 2;

// A front is current within one turn of its date; past STALE_DAYS it is an old line, drawn grey.
export const FRESH_DAYS = 7;
export const STALE_DAYS = 30;
const DAY_MS = 86_400_000;
const FULL_CONTROL = 255; // controlFrac of a hex held from edge to edge
const HASH_BASIS = 0x811c9dc5; // FNV-1a
const HASH_PRIME = 0x01000193;
const HASH_QUANT = 1000;
const HEX_RADIX = 16;

/** One front ready to draw: world polylines, who holds it and its sources. */
export interface FrontRecord {
  id: string;
  controller: string;
  /** Actor index whose colour draws the ribbon (0 = default flame colour). */
  actor: number;
  asOf: string;
  /** Whole days between asOf and the snapshot date (0 when asOf is not before it). */
  age: number;
  /** Older than STALE_DAYS: the line no longer says where the front is. */
  stale: boolean;
  lines: WorldLine[];
  sources: SourceId[];
  length: number;
  /** Point half way along the longest line: where the front's tag sits. */
  mid: { x: number; z: number };
}

/** Whole days from `from` to `to` (YYYY-MM-DD); 0 when either is not a date or `to` is not later. */
export function daysBetween(from: string, to: string): number {
  const days = (Date.parse(to) - Date.parse(from)) / DAY_MS;
  return Number.isFinite(days) && days > 0 ? Math.floor(days) : 0;
}

/** The point half way along the longest of `lines`. */
function midOf(lines: readonly WorldLine[]): { x: number; z: number } {
  const longest = lines.reduce((a, b) => (lineLength(b) > lineLength(a) ? b : a));
  const half = lineLength(longest) / 2;
  let walked = 0;

  for (let i = 2; i < longest.length; i += 2) {
    const dx = longest[i] - longest[i - 2];
    const dz = longest[i + 1] - longest[i - 1];
    const len = Math.hypot(dx, dz);
    if (walked + len >= half && len > 0) {
      const t = (half - walked) / len;
      return { x: longest[i - 2] + dx * t, z: longest[i - 1] + dz * t };
    }
    walked += len;
  }

  return { x: longest[0], z: longest[1] };
}

/**
 * Actor index holding a front: the most common actor among the hexes its vertices fall on that are
 * controlled by someone other than their owner (the occupier), else the most common owner. 0 when
 * neither says.
 */
export function inferActor(lines: readonly WorldLine[], controller: Uint16Array, owner: Uint16Array): number {
  const occupiers = new Map<number, number>();
  const owners = new Map<number, number>();

  for (const line of lines) {
    for (let i = 0; i < line.length; i += 2) {
      const hex = hexAtWorld(line[i], line[i + 1]);
      const votes = controller[hex] > 0 ? occupiers : owners;
      const who = controller[hex] || owner[hex];
      if (who > 0) {
        votes.set(who, (votes.get(who) ?? 0) + 1);
      }
    }
  }

  return topVote(occupiers) || topVote(owners);
}

function topVote(votes: ReadonlyMap<number, number>): number {
  let best = 0;
  let bestVotes = 0;

  for (const [who, n] of votes) {
    if (n > bestVotes) {
      best = who;
      bestVotes = n;
    }
  }
  return best;
}

/** Sourced fronts of a snapshot as drawable records (a front without sources is dropped). */
export function frontRecords(snapshot: WorldSnapshot, actorOf: (id: string) => number): FrontRecord[] {
  const out: FrontRecord[] = [];
  const holderAt = (x: number, z: number): number => {
    const hex = hexAtWorld(x, z);
    // The front runs through a hex only partly held, and this view's owner of a hex the default
    // view gives to someone else is no evidence of who holds it.
    if (snapshot.claim?.[hex] || (snapshot.controlFrac[hex] > 0 && snapshot.controlFrac[hex] < FULL_CONTROL)) {
      return 0;
    }
    return snapshot.controller[hex] || snapshot.owner[hex];
  };

  for (const f of snapshot.fronts ?? []) {
    const lines = contactLines(f.lines.map(toWorld).filter((l) => l.length >= 4), holderAt);
    if (lines.length === 0 || f.sources.length === 0) {
      continue;
    }

    const known = actorOf(f.controller);
    const age = daysBetween(f.asOf, snapshot.date);
    out.push({
      id: f.id,
      controller: f.controller,
      actor: known > 0 ? known : inferActor(lines, snapshot.controller, snapshot.owner),
      asOf: f.asOf,
      age,
      stale: age > STALE_DAYS,
      lines,
      sources: f.sources,
      length: lines.reduce((sum, l) => sum + lineLength(l), 0),
      mid: midOf(lines),
    });
  }

  return out;
}

/** How finely the field is drawn: a sketch while the date is dragged, in full once it rests. */
export type FieldQuality = 'sketch' | 'full';

/** Quality for a snapshot that arrives `gapMs` after the last one. */
export function qualityAfter(gapMs: number): FieldQuality {
  return gapMs < SCRUB_MS ? 'sketch' : 'full';
}

/** FNV-1a over the vertices to a thousandth of a world unit: lines of one shape hash alike. */
function vertexHash(lines: readonly WorldLine[]): string {
  let hash = HASH_BASIS;

  for (const line of lines) {
    for (const v of line) {
      hash = Math.imul(hash ^ Math.round(v * HASH_QUANT), HASH_PRIME);
    }
  }
  return (hash >>> 0).toString(HEX_RADIX);
}

/** Cheap fingerprint of the fronts, so unchanged fronts skip the raster. */
export function frontSignature(fronts: readonly FrontRecord[]): string {
  return fronts
    .map((f) => `${f.id}:${f.asOf}:${f.stale ? 'old' : 'now'}:${f.actor}:${f.lines.length}:${f.length.toFixed(3)}:${vertexHash(f.lines)}`)
    .join('|');
}

interface FrontSample {
  /** Distance to the nearest ribbon line in world units (FAR when none). */
  d: F;
  /** Share of its width the nearest line keeps here: 1 along it, 0 at its ends and past them. */
  taper: F;
  /** Actor index of the ribbon's colour. */
  actor: F;
  /** 0..1 flicker. */
  heat: F;
  /** 1 for an old line (grey, no glow), 0 for a current one. */
  stale: F;
}

/** Distance (world units) from the fragment to the nearest line, and that line's end taper; FAR and 1 outside the field's box. */
function fieldAt(ctx: SurfaceCtx, atlas: Atlas, box: UniformNode<'vec4', THREE.Vector4>, filled: F): { d: F; taper: F } {
  const dx = mod(ctx.xz.x.sub(box.x).add(float(WORLD_W * 1.5)), float(WORLD_W)).sub(WORLD_W / 2);
  const u = dx.div(box.z.mul(2)).add(0.5);
  const v = ctx.xz.y.sub(box.y).div(box.w.mul(2)).add(0.5);
  const inside = u.greaterThanEqual(0).and(u.lessThanEqual(1)).and(v.greaterThanEqual(0)).and(v.lessThanEqual(1));

  // Clamp to the field's texel centres: a linear tap beyond them would blend in the per-hex block
  // (or, for a sketch, the rest of the field block). `filled` is the share of the block written.
  const edge = float(0.5 / FIELD_SIZE).div(filled);
  const inner = (t: F): F => t.clamp(edge, float(1).sub(edge)).mul(filled);
  const uv = vec2(inner(u), inner(v).mul(FIELD_SIZE / ATLAS_H));
  const stored = texture(atlas.texture, uv).level(float(0));
  const near = inside.and(stored.r.lessThan(0.999));
  return { d: select(near, stored.r.mul(FIELD_REACH), float(FAR)), taper: select(near, stored.g, float(1)) };
}

/** Ripple of brightness travelling along the ribbon, 0..1. */
function heatAt(ctx: SurfaceCtx): F {
  const drift = vec2(ctx.time.mul(HEAT_DRIFT[0]), ctx.time.mul(HEAT_DRIFT[1]));
  const n = worldNoise(ctx.xz.add(drift), HEAT_FREQ).mul(0.6).add(worldNoise(ctx.xz.sub(drift.mul(1.7)), HEAT_FREQ * 2.3).mul(0.4));
  const breath = sin(ctx.time.mul(Math.PI * 2 * BREATH_HZ).add(n.mul(6))).mul(BREATH).add(1);
  return mix(float(HEAT_MIN), float(1), n).mul(breath);
}

/** Sparks lifting off the line (0..1): sparse dots that drift north and twinkle. */
function embersAt(ctx: SurfaceCtx, d: F): F {
  const p = vec2(ctx.xz.x.mul(EMBER_FREQ), ctx.xz.y.mul(EMBER_FREQ).add(ctx.time.mul(EMBER_RISE * EMBER_FREQ)));
  const cell = floor(p);
  const f = fract(p);
  const at = vec2(hash12(cell), hash12(cell.add(vec2(17.3, 5.1)))).mul(0.5).add(0.25);
  const alive = select(hash12(cell.add(vec2(41.7, 9.3))).greaterThan(EMBER_DENSITY), float(1), float(0));
  const twinkle = sin(ctx.time.mul(hash12(cell.add(vec2(3.3, 8.8))).mul(4).add(3)).add(hash12(cell).mul(40))).mul(0.5).add(0.5);
  const dot = smoothstep(EMBER_SIZE, 0, length(f.sub(at)));
  const near = smoothstep(EMBER_REACH, 0.12, d);
  return dot.mul(alive).mul(twinkle).mul(near).mul(smoothstep(EMBER_FADE_PX[0], EMBER_FADE_PX[1], ctx.px));
}

/** The fronts that share one field: longest first, all held by the first one's actor and all current or all old, inside MAX_SPAN. */
export function pickFronts(fronts: readonly FrontRecord[]): FrontRecord[] {
  const sorted = [...fronts].sort((a, b) => b.length - a.length);
  const chosen: FrontRecord[] = [];

  for (const front of sorted) {
    if (chosen.length > 0 && (chosen[0].actor !== front.actor || chosen[0].stale !== front.stale)) {
      continue;
    }

    const box = fieldBox([...chosen, front].flatMap((f) => f.lines), FIELD_REACH, 0, FIELD_SIZE);
    if (!box || box.halfW * 2 > MAX_SPAN || box.halfH * 2 > MAX_SPAN) {
      continue;
    }
    chosen.push(front);
  }
  return chosen;
}

export class FrontField {
  private readonly atlas: Atlas;
  private readonly scratch = new Uint8Array(FIELD_SIZE * FIELD_SIZE);
  private readonly taper = new Uint8Array(FIELD_SIZE * FIELD_SIZE);
  private readonly box = uniform(new THREE.Vector4(0, INACTIVE_Z, 1, 1));
  private readonly actor = uniform(0);
  private readonly stale = uniform(0);
  private readonly filled = uniform(1);
  private readonly cache = new WeakMap<object, FrontSample>();
  private signature = '';
  private shown: FrontRecord[] = [];
  private drawn: FieldQuality = 'full';

  constructor(atlas: Atlas) {
    this.atlas = atlas;
  }

  /** Fronts drawn as ribbons in the last snapshot. */
  fronts(): readonly FrontRecord[] {
    return this.shown;
  }

  /** Draw these fronts (those pickFronts keeps); unchanged fronts cost nothing. */
  set(fronts: readonly FrontRecord[], quality: FieldQuality): void {
    const chosen = pickFronts(fronts);
    const sig = frontSignature(chosen);
    const sharper = quality === 'full' && this.drawn === 'sketch';
    if (sig === this.signature && !sharper) {
      return;
    }

    this.signature = sig;
    this.shown = chosen;
    this.draw(quality);
  }

  /** Redraw a sketch in full; does nothing when the field is already full. */
  refine(): void {
    if (this.drawn === 'sketch') {
      this.draw('full');
    }
  }

  private draw(quality: FieldQuality): void {
    const lines = this.shown.flatMap((f) => f.lines);
    const box = fieldBox(lines, FIELD_REACH, 0, FIELD_SIZE);
    if (!box) {
      this.box.value.set(0, INACTIVE_Z, 1, 1);
      this.actor.value = 0;
      this.stale.value = 0;
      this.drawn = 'full';
      return;
    }

    // Square texel grid: use the whole field whatever the box aspect.
    const size = quality === 'sketch' ? SKETCH_SIZE : FIELD_SIZE;
    const grid = { ...box, nx: size, ny: size };
    const raster = this.scratch.subarray(0, size * size);
    const taper = this.taper.subarray(0, size * size);
    rasterField(lines, grid, FIELD_REACH, raster, taper);
    this.write(raster, taper, size);
    this.box.value.set(grid.cx, grid.cz, grid.halfW, grid.halfH);
    this.filled.value = size / FIELD_SIZE;
    this.actor.value = this.shown[0].actor;
    this.stale.value = this.shown[0].stale ? 1 : 0;
    this.drawn = quality;
  }

  private sample(ctx: SurfaceCtx): FrontSample {
    const hit = this.cache.get(ctx.hex);
    if (hit) {
      return hit;
    }

    const field = fieldAt(ctx, this.atlas, this.box, float(this.filled));
    const sample = { d: field.d.toVar('frontD'), taper: field.taper.toVar('frontTaper'), actor: float(this.actor), heat: heatAt(ctx), stale: float(this.stale) };
    this.cache.set(ctx.hex, sample);
    return sample;
  }

  /** 1 where a ribbon replaces the hex-edge front line, else 0 (for borders.ts). */
  cover(ctx: SurfaceCtx): F {
    return smoothstep(COVER_FAR, COVER_NEAR, this.sample(ctx).d);
  }

  layer(): SurfaceLayer {
    const bodyColor = (ctx: SurfaceCtx, s: FrontSample): V3 => {
      const flame = rgb(DEFAULT_COLOR);
      const held = ctx.data.actorColor(s.actor, 0);
      const live = mix(select(s.actor.greaterThan(0.5), held, flame), vec3(1), BODY_LIGHTEN);
      return mix(live, rgb(STALE_COLOR), s.stale);
    };
    const widths = (ctx: SurfaceCtx, s: FrontSample): { body: F; core: F; casing: F } => {
      const aa = ctx.px.mul(0.75);
      // A line's end narrows and fades (s.taper) instead of ending in a round cap.
      const bodyW = ctx.px.mul(BODY_MIN_PX).max(BODY_W).mul(s.taper);
      const coreW = ctx.px.mul(CORE_MIN_PX).max(CORE_W).mul(s.heat.mul(0.4).add(0.8)).mul(s.taper);
      const casingW = bodyW.mul(CASING_SCALE);
      return {
        body: smoothstep(bodyW.add(aa), bodyW.sub(aa), s.d).mul(s.taper),
        core: smoothstep(coreW.add(aa), coreW.sub(aa), s.d).mul(float(1).sub(s.stale)).mul(s.taper),
        casing: smoothstep(casingW.add(aa), casingW.sub(aa), s.d).mul(s.taper),
      };
    };

    return {
      name: 'fronts',
      order: 310,
      color: (ctx) => {
        const s = this.sample(ctx);
        const w = widths(ctx, s);
        const scorch = smoothstep(SCORCH_REACH, 0.05, s.d).mul(SCORCH_STRENGTH).mul(s.taper).max(w.casing.mul(CASING_STRENGTH));
        let c: V3 = mix(ctx.color, ctx.color.mul(vec3(...SCORCH_TINT)), scorch);
        c = mix(c, bodyColor(ctx, s), w.body);
        return mix(c, rgb(HOT_COLOR), w.core);
      },
      emissive: (ctx) => {
        const s = this.sample(ctx);
        const w = widths(ctx, s);
        const halo = smoothstep(HALO_REACH, 0, s.d).pow(2).mul(HALO_GLOW).mul(s.taper);
        const glow = mix(float(1), float(STALE_GLOW), s.stale);
        const body = bodyColor(ctx, s).mul(w.body.mul(BODY_GLOW).add(halo)).mul(s.heat).mul(glow);
        const core = rgb(HOT_COLOR).mul(w.core.mul(CORE_GLOW).mul(s.heat));
        const embers = embersAt(ctx, s.d).mul(EMBER_GLOW).mul(float(1).sub(s.stale)).mul(s.taper);
        return body.add(core).add(rgb(HOT_COLOR).mul(embers));
      },
    };
  }

  /** Copy a size × size raster into the corner of the atlas's R channel and its taper into G (B and A are unused there). */
  private write(raster: Uint8Array, taper: Uint8Array, size: number): void {
    const out = this.atlas.data;
    for (let j = 0; j < size; j++) {
      const from = j * size;
      const to = j * FIELD_SIZE;
      for (let i = 0; i < size; i++) {
        out[(to + i) * CHANNELS] = raster[from + i];
        out[(to + i) * CHANNELS + TAPER_CHANNEL] = taper[from + i];
      }
    }
    this.atlas.touch();
  }
}
